import { Injectable, isDevMode } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { API, Auth } from 'aws-amplify';
import { AppAnalyticsService } from 'src/app-analytics.service';
import { CorpClientsService } from '../corp-clients.service';
import { PortalSettingsService } from '../portal-settings.service';
import { AuthMonitorService } from './auth-monitor.service';
import { environment } from '../../environments/environment';
import { SSOCheckResponse, SSOTokenExchangeRequest, SSOTokenExchangeResponse } from './sso-api.models';
import { DevAWSSettings, ProdAWSSettings } from 'src/awsconfig';
import { CognitoIdentityServiceProvider } from 'aws-sdk';

export interface SSOProvider {
  id: string;
  name: string;
  displayName: string;
  enabled: boolean;
  authConfig: {
    issuer?: string;
    clientId?: string;
    tenantId?: string;
    redirectUri?: string;
    scope?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class SsoService {
  private providersSubject = new BehaviorSubject<SSOProvider[]>([]);
  public providers$ = this.providersSubject.asObservable();
  
  private currentProvider: SSOProvider | null = null;
  private apiBaseUrl: string;
  
  /**
   * Extract the Cognito identity provider name from ID token claims
   * First tries the 'identities' attribute (Cognito's built-in federated identity info)
   * Falls back to extracting from username if needed
   */
  private extractProviderFromClaims(claims: any): string | null {
    // Option 1: Check the identities attribute (most reliable)
    if (claims.identities) {
      try {
        const identities = typeof claims.identities === 'string' 
          ? JSON.parse(claims.identities) 
          : claims.identities;
        
        if (Array.isArray(identities) && identities.length > 0) {
          const primaryIdentity = identities.find((id: any) => id.primary) || identities[0];
          if (primaryIdentity?.providerName) {
            console.log('[SSO] Provider extracted from identities claim:', primaryIdentity.providerName);
            return primaryIdentity.providerName;
          }
        }
      } catch (e) {
        console.warn('[SSO] Failed to parse identities claim:', e);
      }
    }
    
    // Option 2: Extract from username (legacy fallback)
    // Cognito federated username format: {provider-name}_{subject-id}
    if (claims['cognito:username'] && claims['cognito:username'].includes('_')) {
      const username = claims['cognito:username'];
      const separatorIndex = username.indexOf('_');
      const provider = username.substring(0, separatorIndex);
      console.log('[SSO] Provider extracted from cognito:username:', provider);
      return provider;
    }
    
    console.warn('[SSO] Could not extract provider from claims:', {
      hasIdentities: !!claims.identities,
      hasCognitoUsername: !!claims['cognito:username'],
      username: claims['cognito:username']
    });
    return null;
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private analyticsService: AppAnalyticsService,
    private corpService: CorpClientsService,
    private portalSettingsService: PortalSettingsService,
    private authMonitorService: AuthMonitorService
  ) {
    // Get API base URL from awsconfig based on environment
    const aws = (environment.production ? new ProdAWSSettings() : new DevAWSSettings()).value as any;
    const userEndpoint = aws.API.endpoints.find((e: any) => e.name === 'SalesMailUser');
    this.apiBaseUrl = userEndpoint?.endpoint || 'https://dev-app-api.mysalesmail.com/prod';
    console.log('[SSO] API base URL configured:', this.apiBaseUrl);
    
    this.initializeProviders();
  }

  private initializeProviders() {
    // Static/well-known providers. Any Cognito IDP name containing 'salesmail-'
    // (e.g., dev-salesmail-saml-office365, prod-salesmail-okta-12345) is created
    // dynamically by getProvider() so it never needs to be listed here.
    const providers: SSOProvider[] = [
      {
        id: 'office365',
        name: 'office365',
        displayName: 'Microsoft Office 365',
        enabled: true,
        authConfig: { scope: 'openid profile email' }
      },
      {
        id: 'okta',
        name: 'okta',
        displayName: 'Okta',
        enabled: true,
        authConfig: { scope: 'openid profile email' }
      }
    ];

    this.providersSubject.next(providers);
  }

  /**
   * Email-first SSO check (unauthenticated).
   * Calls GET /auth/sso/check?email= to determine whether the email belongs to a
   * federated SSO user or a traditional Cognito username/password user.
   *
   * Falls back to { authType: 'traditional' } on any error so the password path
   * always remains usable.
   */
  async checkSSO(email: string): Promise<SSOCheckResponse> {
    const normalizedEmail = (email || '').trim().toLowerCase();
    try {
      const response = await API.get('SalesMailUser', '/auth/sso/check', {
        queryStringParameters: { email: normalizedEmail }
      });

      // Unwrap successResponse() envelopes ({ value: {...} }) if present
      const data = response && typeof response === 'object' && 'value' in response
        ? (response as any).value
        : response;

      if (data?.authType === 'sso') {
        return { authType: 'sso', ssoProvider: data.ssoProvider };
      }
      return { authType: 'traditional' };
    } catch (error: any) {
      console.warn('[SSO] checkSSO failed, defaulting to traditional auth:', error?.message || error);
      return { authType: 'traditional' };
    }
  }

  /**
   * Authenticate user with a provided SSO token (direct token flow)
   */
  async authenticateWithToken(providerId: string, token: string): Promise<void> {
    try {
      this.currentProvider = this.getProvider(providerId);
      if (!this.currentProvider) {
        throw new Error(`Unknown SSO provider: ${providerId}`);
      }

      console.log('[SSO] Calling Lambda with token...');
      
      // Let the Lambda handle everything - token validation, user lookup, client mapping
      const response = await this.http.post<SSOTokenExchangeResponse>(`${this.apiBaseUrl}/auth/sso/token-exchange`, {
        accessToken: token,
        provider: providerId  // Let Lambda figure out everything else
      }).toPromise();

      console.log('Lambda response received:', response);

      // Handle wrapped response from successResponse()
      let authData = response;
      if (response && typeof response === 'object' && 'value' in response) {
        authData = (response as any).value;
        console.log('Unwrapped response data:', authData);
      }

      if (!authData?.cognitoTokens) {
        const errorMsg = authData?.error || 'Authentication failed - no tokens received';
        console.error('Authentication failed:', errorMsg);
        throw new Error(`Authentication failed: ${errorMsg}`);
      }

      if (!authData.user) {
        throw new Error('Authentication failed: No user data received');
      }

      // Set Amplify Auth session with returned tokens
      await this.setAmplifySession(authData);

    } catch (error: any) {
      console.error('SSO authentication error:', error);
      this.analyticsService.log('login', `SSO_Token_Failed_${providerId}`);
      
      // Provide more specific error messages
      if (error.status === 0) {
        throw new Error('Network error: Unable to connect to authentication server. Please check your connection and try again.');
      } else if (error.status >= 400 && error.status < 500) {
        throw new Error(`Authentication failed: ${error.error?.message || error.message || 'Invalid request'}`);
      } else if (error.status >= 500) {
        throw new Error('Server error: Please try again later or contact support if the problem persists.');
      } else {
        throw new Error(`Token authentication failed: ${error.message}`);
      }
    }
  }

  /**
   * Exchange OAuth authorization code for tokens (OAuth code flow)
   */
  async exchangeAuthorizationCode(providerId: string, code: string, state?: string): Promise<void> {
    try {
      this.currentProvider = this.getProvider(providerId);
      if (!this.currentProvider) {
        throw new Error(`Unknown SSO provider: ${providerId}`);
      }

      // First, we need to exchange the code for an access token using Microsoft's token endpoint
      const tokenResponse = await this.exchangeCodeWithMicrosoft(providerId, code);
      
      // Now use the access token in our normal flow
      await this.authenticateWithToken(providerId, tokenResponse.access_token);

    } catch (error: any) {
      this.analyticsService.log('login', `SSO_Code_Failed_${providerId}`);
      throw error;
    }
  }

  private async exchangeCodeWithMicrosoft(providerId: string, code: string): Promise<any> {
    try {
      // This is where you'd normally use Cognito's federated identity to handle the code exchange
      // But for now, we'll do a direct exchange with Microsoft
      
      // Note: You'll need the client ID and secret from your Azure AD app registration
      // These should come from environment variables or be configured in Cognito
      
      throw new Error('OAuth code flow not yet fully implemented. Please use direct token flow for now.');
      
      // Future implementation would:
      // 1. Use Cognito's federated identity to exchange the code
      // 2. OR exchange directly with Microsoft's token endpoint
      // 3. Return the access token for our normal flow
      
    } catch (error: any) {
      throw new Error(`Code exchange failed: ${error.message}`);
    }
  }

  private async setAmplifySession(response: SSOTokenExchangeResponse): Promise<void> {
    try {
      console.log('[SSO] Setting Amplify session with response:', response);

      // Use current env awsconfig values (do not override Amplify global config here)
      const aws = (environment.production ? new ProdAWSSettings() : new DevAWSSettings()).value as any;
      const userPoolId: string = aws.Auth.userPoolId;
      const userPoolWebClientId: string = aws.Auth.userPoolWebClientId;

      // CRITICAL FIX 1: Clean up any existing duplicate/conflicting token storage
      console.log('[SSO] Cleaning up existing token storage to prevent duplication...');
      this.cleanupExistingTokens(userPoolWebClientId);

      // Create a proper Cognito User session using the tokens from Lambda
      const { CognitoUserPool, CognitoUser, CognitoUserSession, CognitoIdToken, CognitoAccessToken, CognitoRefreshToken } = await import('amazon-cognito-identity-js');

      const idToken = new CognitoIdToken({ IdToken: response.cognitoTokens.idToken });
      const accessToken = new CognitoAccessToken({ AccessToken: response.cognitoTokens.accessToken });
      const refreshToken = new CognitoRefreshToken({ RefreshToken: response.cognitoTokens.refreshToken });

      const session = new CognitoUserSession({
        IdToken: idToken,
        AccessToken: accessToken,
        RefreshToken: refreshToken
      });

      const userPool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: userPoolWebClientId });

      // CRITICAL FIX 2: Use the Cognito UUID (from sub claim) as the username, not the federated username
      const cognitoUsername = response.user?.cognitoId || response.user?.id?.toString();
      if (!cognitoUsername) {
        throw new Error('Could not determine Cognito username from response');
      }

      const cognitoUser = new CognitoUser({
        Username: cognitoUsername,  // Use Cognito UUID, not federated username
        Pool: userPool
      });

      cognitoUser.setSignInUserSession(session);

      // CRITICAL FIX 3: Use Amplify's proper session storage mechanism
      try {
        // Set the current user for Amplify's Auth module to recognize
        (Auth as any).user = cognitoUser;
        
        // Trigger Amplify's internal storage mechanism
        const amplifyUser = await Auth.currentAuthenticatedUser();
        console.log('[SSO] ✅ Amplify recognized user via internal cache:', amplifyUser?.username);
        
        // Force Amplify to sync its internal state
        await Auth.currentSession();
        console.log('[SSO] ✅ Amplify session validated internally');
        
      } catch (internalError) {
        console.warn('[SSO] Amplify internal cache failed, using controlled manual storage:', internalError);
        
        // CRITICAL FIX 4: Controlled manual storage - only store under Cognito UUID
        const lastAuthUser = `CognitoIdentityServiceProvider.${userPoolWebClientId}.LastAuthUser`;
        const accessTokenKey = `CognitoIdentityServiceProvider.${userPoolWebClientId}.${cognitoUsername}.accessToken`;
        const idTokenKey = `CognitoIdentityServiceProvider.${userPoolWebClientId}.${cognitoUsername}.idToken`;
        const refreshTokenKey = `CognitoIdentityServiceProvider.${userPoolWebClientId}.${cognitoUsername}.refreshToken`;
        const clockDriftKey = `CognitoIdentityServiceProvider.${userPoolWebClientId}.${cognitoUsername}.clockDrift`;
        const userDataKey = `CognitoIdentityServiceProvider.${userPoolWebClientId}.${cognitoUsername}.userData`;

        // CRITICAL FIX 7: Build userData from scratch with correct Username format
        // Extract provider name from response or current provider context
        const providerName = response.provider || this.currentProvider?.id || 'office365';
        
        const userData = {
          UserAttributes: [
            { Name: 'sub', Value: cognitoUsername }, // Use cognito UUID 
            { Name: 'email', Value: response.user?.email },
            { Name: 'name', Value: response.user?.firstName && response.user?.lastName ? 
                `${response.user.firstName} ${response.user.lastName}`.trim() : 
                response.user?.email?.split('@')[0] || 'SSO User' },
            { Name: 'custom:client_id', Value: response.user?.id?.toString() || '1' },
            { Name: 'identities', Value: JSON.stringify([{
                userId: response.user?.cognitoId || cognitoUsername,
                providerName: providerName,
                providerType: 'OIDC',
                issuer: null,
                primary: true,
                dateCreated: Date.now().toString()
              }])
            },
            { Name: 'email_verified', Value: 'true' }
          ].filter(attr => attr.Value), // Remove undefined values
          Username: cognitoUsername  // CRITICAL: Use Cognito UUID, NOT federated format
        };

        console.log('[SSO] Constructed userData with Username:', userData.Username);

        localStorage.setItem(lastAuthUser, cognitoUsername);
        localStorage.setItem(accessTokenKey, response.cognitoTokens.accessToken);
        localStorage.setItem(idTokenKey, response.cognitoTokens.idToken);
        localStorage.setItem(refreshTokenKey, response.cognitoTokens.refreshToken);
        localStorage.setItem(clockDriftKey, '0'); // Amplify expects this for token validation
        localStorage.setItem(userDataKey, JSON.stringify(userData)); // Store user metadata in Amplify format
        
        // CRITICAL FIX 6: Set correct Amplify flags for SSO session
        // Don't set hosted UI flags since this is a token exchange, not hosted UI flow
        localStorage.removeItem('amplify-redirected-from-hosted-ui');
        localStorage.removeItem('amplify-signin-with-hostedUI');
        
        console.log('[SSO] ✅ Controlled manual storage completed - tokens stored only under Cognito UUID');
      }

      console.log('[SSO] ✅ Amplify session set directly with Cognito tokens');

      const authenticatedUser = await Auth.currentAuthenticatedUser();
      console.log('[SSO] ✅ Authenticated user confirmed:', authenticatedUser?.username);

      console.log('[SSO] Getting AWS credentials from Amplify...');
      
      // CRITICAL FIX 5: Only clear cache if credentials are actually missing/broken
      // Don't break working credentials by clearing unnecessarily
      let needsCredentialFix = false;
      let existingCredentials;
      
      try {
        // First check if we already have working credentials
        existingCredentials = await Auth.currentCredentials();
        
        if (!existingCredentials?.accessKeyId || !existingCredentials?.sessionToken) {
          console.log('[SSO] ✅ No working credentials found, will attempt to establish fresh mapping');
          needsCredentialFix = true;
        } else {
          console.log('[SSO] ✅ Working credentials already exist, preserving them:', {
            identityId: existingCredentials.identityId,
            hasAccessKey: !!existingCredentials.accessKeyId,
            hasSessionToken: !!existingCredentials.sessionToken,
            authenticated: existingCredentials.authenticated
          });
        }
      } catch (credentialError: any) {
        console.log('[SSO] ✅ Error checking existing credentials, will establish fresh mapping:', credentialError?.message);
        needsCredentialFix = true;
      }
      
      // Only clear cache and rebuild if we actually need to fix broken credentials
      if (needsCredentialFix) {
        try {
          // Clear any stale credential cache
          const credentials = (Auth as any).Credentials || (Auth as any).credentials;
          if (credentials && credentials.clear) {
            await credentials.clear();
            console.log('[SSO] ✅ Cleared stale Identity Pool credential cache');
          }
          
          // Force Amplify to establish fresh Identity Pool mapping
          existingCredentials = await Auth.currentCredentials();
          console.log('[SSO] ✅ Established fresh credential mapping');
          
        } catch (establishError: any) {
          console.error('[SSO] ❌ Failed to establish fresh Identity Pool credential mapping:', establishError?.message);
          // Don't throw - let the user continue, they might work with existing session
        }
      }
      
      // PHASE 2: Store BOTH identity mapping formats to eliminate refresh ambiguity
      if (existingCredentials && existingCredentials.identityId) {
        try {
          const region = aws.Auth.region;
          const identityPoolId = aws.Auth.identityPoolId;
          const idToken = response.cognitoTokens.idToken;
          
          this.ensureBothIdentityMappingFormats({
            userPoolId,
            identityPoolId,
            region,
            identityId: existingCredentials.identityId,
            idToken
          });
          
        } catch (storageError: any) {
          console.warn('[SSO] ⚠️ Could not store Identity Pool mapping for persistence:', storageError?.message);
        }
      }
      
      console.log('[SSO] ✅ Final AWS credentials state:', {
        hasAccessKey: !!existingCredentials?.accessKeyId,
        hasSecretKey: !!existingCredentials?.secretAccessKey,
        hasSessionToken: !!existingCredentials?.sessionToken,
        identityId: existingCredentials?.identityId,
        accessKeyPrefix: existingCredentials?.accessKeyId?.substring(0, 4),
        authenticated: existingCredentials?.authenticated,
        expiration: existingCredentials?.expiration
      });

      if (!existingCredentials?.accessKeyId || !existingCredentials?.sessionToken) {
        console.warn('[SSO] ⚠️ AWS credentials missing required components. This will cause API calls to fail.');
      }

      this.analyticsService.log('login', `SSO_Success_${this.currentProvider?.id}`);

      console.log('[SSO] Loading authentication context...');
      this.portalSettingsService.loadData();
      this.corpService.loadUser();
      this.authMonitorService.reLoad();

      console.log('[SSO] ✅ SSO authentication complete - user should now have full access');
      
      // FINAL VERIFICATION: Check for any remaining duplicate tokens that could cause issues
      this.verifyCleanTokenStorage(userPoolWebClientId);

    } catch (error: any) {
      console.error('[SSO] Failed to set Amplify session:', error);
      throw new Error(`Session creation failed: ${error.message}`);
    }
  }

  /**
   * Final verification to ensure clean token storage
   */
  private verifyCleanTokenStorage(userPoolWebClientId: string): void {
    const allKeys = Object.keys(localStorage);
    const cognitoKeys = allKeys.filter(key => key.includes('CognitoIdentityServiceProvider'));
    
    // Check for any remaining duplicate or problematic tokens
    const duplicateUsers = new Set();
    const problemKeys = [];
    
    cognitoKeys.forEach(key => {
      // Check for federated usernames that shouldn't be there
      if (key.includes('dev-salesmail-office365_') || key.includes('office365_')) {
        problemKeys.push(key);
      }
      
      // Check for wrong client IDs
      if (key.includes('CognitoIdentityServiceProvider') && 
          !key.includes(userPoolWebClientId) &&
          !key.includes('LastAuthUser')) {
        problemKeys.push(key);
      }
      
      // Extract username from token keys to check for duplicates
      const match = key.match(/CognitoIdentityServiceProvider\.[^.]+\.([^.]+)\./);
      if (match && match[1] !== 'LastAuthUser') {
        duplicateUsers.add(match[1]);
      }
    });
    
    if (problemKeys.length > 0) {
      console.warn('[SSO] ⚠️  Found problematic token keys after cleanup:', problemKeys);
      // Clean them up
      problemKeys.forEach(key => {
        console.log(`[SSO] Removing remaining problematic token: ${key}`);
        localStorage.removeItem(key);
      });
    }
    
    if (duplicateUsers.size > 1) {
      console.warn('[SSO] ⚠️  Found multiple user tokens, this could cause refresh issues:', Array.from(duplicateUsers));
    } else {
      console.log('[SSO] ✅ Verified clean token storage - only one user token set exists');
    }
  }

  private parseJwtPayload(token: string): any {
    try {
      const base64Payload = token.split('.')[1];
      const payload = JSON.parse(atob(base64Payload));
      return payload;
    } catch (error) {
      console.error('Failed to parse JWT payload:', error);
      return {};
    }
  }

  // Poll until Amplify completes Hosted UI code -> tokens exchange
  private async waitForAmplifySession(timeoutMs = 15000) {
    const start = Date.now();
    while (true) {
      try {
        // Nudge Amplify to complete any pending exchange
        await (Auth as any).fetchAuthSession?.();
        const session = await Auth.currentSession();
        if (session?.isValid?.()) return session;
      } catch {}
      if (Date.now() - start > timeoutMs) throw new Error('Authentication did not complete.');
      await new Promise(r => setTimeout(r, 250));
    }
  }

  /**
   * Initiate OAuth flow by redirecting to provider via Cognito Hosted UI
   * This ensures PKCE is generated and the code can be exchanged on return
   * 
   * NOTE: The redirect URI is static (/sso/office365). The actual IDP used is passed via
   * the identity_provider query parameter by Cognito and extracted after authentication
   * from the identities claim.
   */
  async initiateOAuthFlow(providerId: string): Promise<void> {
    console.log('[SSO] initiateOAuthFlow, providerId: ', providerId);
    // Map our logical provider to the Cognito IdP name
    const cognitoProvider = this.mapToCognitoProvider(providerId);

    // Get OAuth config from awsconfig
    const aws = (environment.production ? new ProdAWSSettings() : new DevAWSSettings()).value as any;
    
    // Use the standard redirectSignIn from awsconfig for ALL providers.
    // The identity_provider param tells Cognito which IDP to use, not the redirect path.
    // This keeps SAML working the same way OIDC always did.
    const redirectSignIn = Array.isArray(aws.Auth.oauth.redirectSignIn)
      ? aws.Auth.oauth.redirectSignIn[0]
      : aws.Auth.oauth.redirectSignIn;
    const redirectSignOut = Array.isArray(aws.Auth.oauth.redirectSignOut)
      ? aws.Auth.oauth.redirectSignOut[0]
      : aws.Auth.oauth.redirectSignOut;

    console.log('[SSO] Using redirect URI:', redirectSignIn);
    console.log('[SSO] Cognito IDP name:', cognitoProvider);

    // Force a single redirect URI and fully specify oauth to avoid array merges/comma-joins
    try {
      
      Auth.configure({
        oauth: {
          domain: aws.Auth.oauth.domain,
          scope: aws.Auth.oauth.scope,
          responseType: aws.Auth.oauth.responseType,
          redirectSignIn,
          redirectSignOut
        }
      } as any);
      const cfg = (Auth as any).configure?.() || {};
      console.log('[SSO] Using Hosted UI oauth config', cfg.oauth || cfg.Auth?.oauth);
    } catch (e) {
      console.warn('[SSO] Failed to override oauth config', e);
    }

    console.log('[SSO] Redirecting to Hosted UI via provider:', cognitoProvider);

    // Try using Amplify's built-in federatedSignIn but ensure oauth config is properly set
    try {
      // First attempt: Use the provider parameter
      await Auth.federatedSignIn({ provider: cognitoProvider } as any);
    } catch (e) {
      console.warn('[SSO] federatedSignIn with provider failed, trying customProvider:', e);
      try {
        // Second attempt: Use customProvider parameter
        await Auth.federatedSignIn({ customProvider: cognitoProvider } as any);
      } catch (e2) {
        console.warn('[SSO] federatedSignIn with customProvider also failed, trying direct hosting UI:', e2);
        
        // Third attempt: Direct Hosted UI URL (fallback)
        const aws = (environment.production ? new ProdAWSSettings() : new DevAWSSettings()).value as any;
          
        const hostedUiUrl = `https://${aws.Auth.oauth.domain}/oauth2/authorize` +
          `?identity_provider=${encodeURIComponent(cognitoProvider)}` +
          `&redirect_uri=${encodeURIComponent(redirectSignIn)}` +
          `&response_type=code` +
          `&client_id=${aws.Auth.userPoolWebClientId}` +
          `&scope=${encodeURIComponent(aws.Auth.oauth.scope.join(' '))}`;
        
        console.log('[SSO] Using direct Hosted UI URL as fallback:', hostedUiUrl);
        window.location.href = hostedUiUrl;
      }
    }
  }

  /**
   * Map a logical provider ID to the actual Cognito Identity Provider name
   * 
   * DESIGN: Supports both generic and client-specific IDP names:
   * - Generic 'office365' → maps to default portal IDP (prod-salesmail-office365 or dev-salesmail-office365)
   * - Specific IDP names like 'prod-salesmail-office365-58717' → passed through as-is
   * 
   * This enables:
   * 1. Internal portal users: /sso/office365 → uses default HeartLegacy tenant
   * 2. Client-specific users: /sso/prod-salesmail-office365-58717 → uses client's O365 tenant
   * 3. Future: Email-based lookup can return specific IDP name for dynamic routing
   */
  private mapToCognitoProvider(providerId: string): string {
    const raw = (providerId || '').toLowerCase().trim();
    
    // If it already looks like a full Cognito IDP name (contains our naming pattern), use it as-is
    // Pattern: dev-salesmail-saml-office365, prod-salesmail-office365-58717, etc.
    if (raw.includes('salesmail-')) {
      console.log('[SSO] Using explicit IDP name:', raw);
      return raw;
    }
    
    // Determine base provider name based on environment for generic requests
    const baseProvider = environment.production ? 'prod-salesmail-office365' : 'dev-salesmail-office365';
    const baseOktaProvider = environment.production ? 'prod-salesmail-okta' : 'dev-salesmail-okta';
    
    // Map generic provider names to environment-specific default IDP
    const map: Record<string, string> = {
      office365: baseProvider,
      microsoft: baseProvider,
      azure: baseProvider,
      okta: baseOktaProvider
    };
    
    const result = map[raw] || baseProvider; // Default to base provider if unrecognized
    console.log('[SSO] Mapped generic provider:', raw, '→', result);
    return result;
  }

  getEnabledProviders(): SSOProvider[] {
    return this.providersSubject.value.filter(p => p.enabled);
  }

  logout(): void {
    this.currentProvider = null;
    Auth.signOut();
  }

  /**
   * Determine whether the currently authenticated user signed in via SSO (federated).
   * Inspects the current ID token claims for the Cognito 'identities' attribute.
   */
  async isCurrentUserSso(): Promise<boolean> {
    try {
      const session = await Auth.currentSession();
      const claims = session?.getIdToken()?.payload || {};
      return !!this.extractProviderFromClaims(claims);
    } catch (error: any) {
      console.warn('[SSO] isCurrentUserSso check failed:', error?.message || error);
      return false;
    }
  }

  /**
   * Global sign-out for SSO users. Clears the local Amplify session and redirects to
   * the Cognito Hosted UI /logout endpoint, which also invalidates the federated
   * (Microsoft) session so the user is fully signed out.
   */
  async ssoSignOut(): Promise<void> {
    this.currentProvider = null;
    try {
      await Auth.signOut({ global: true } as any);
    } catch (error: any) {
      console.warn('[SSO] Amplify global signOut failed, continuing to Hosted UI logout:', error?.message || error);
    }

    const aws = (isDevMode() ? new DevAWSSettings() : new ProdAWSSettings() ).value as any;
    const domain: string = aws.Auth.oauth.domain;
    const clientId: string = aws.Auth.userPoolWebClientId;
    const logoutUri = Array.isArray(aws.Auth.oauth.redirectSignOut)
      ? aws.Auth.oauth.redirectSignOut[0]
      : aws.Auth.oauth.redirectSignOut;

    // Cognito /logout supports two mutually exclusive modes:
    //   1. Sign-out only:    client_id + logout_uri
    //   2. Sign-out + re-auth: client_id + redirect_uri + response_type + scope
    // Sending both logout_uri and redirect_uri forces Cognito into mode 2 and it
    // then rejects the request with "Required String parameter 'response_type' is
    // not present". We only want a clean sign-out, so send logout_uri alone.
    const logoutUrl = `https://${domain}/logout` +
      `?client_id=${encodeURIComponent(clientId)}` +
      `&logout_uri=${encodeURIComponent(logoutUri)}`;

    console.log('[SSO] Redirecting to Cognito Hosted UI logout:', logoutUrl);
    window.location.href = logoutUrl;
  }

  isAuthenticated(): boolean {
    return false; // This will be determined by Amplify Auth state
  }

  getUser(): any {
    return null; // This will be handled by your existing user services
  }

  // SAML Support Methods (Future Implementation)
  async processSamlResponse(provider: string, samlResponse: string, relayState?: string): Promise<void> {
    try {
      // TODO: Implement SAML response processing
      // This would decode the base64 SAML response and validate it
      console.log('Processing SAML response for provider:', provider);
      
      // For now, throw an error to indicate this is not yet implemented
      throw new Error('SAML authentication not yet implemented. Please use OIDC providers.');
      
      // Future implementation would:
      // 1. Decode base64 SAML response
      // 2. Validate SAML signature and assertions
      // 3. Extract user claims (email, name, etc.)
      // 4. Call backend API to exchange for Cognito tokens
      // 5. Set Amplify Auth session
      
    } catch (error: any) {
      console.error('SAML response processing failed:', error);
      throw new Error(`SAML authentication failed: ${error.message}`);
    }
  }

  async processSamlRequest(provider: string, samlRequest: string, relayState?: string): Promise<void> {
    try {
      // TODO: Handle SAML redirect binding requests (rare scenario)
      console.log('Processing SAML request for provider:', provider);
      throw new Error('SAML request processing not yet implemented.');
    } catch (error: any) {
      console.error('SAML request processing failed:', error);
      throw new Error(`SAML request processing failed: ${error.message}`);
    }
  }

  async initiateSamlFlow(provider: string): Promise<void> {
    // AWS Cognito abstracts SAML behind its OAuth2/OIDC facade.
    // The SAML provider name (e.g., 'dev-salesmail-saml-office365') is passed to
    // Cognito's /authorize endpoint via identity_provider parameter.
    // Cognito handles the SAML redirect to the IdP automatically.
    console.log('[SSO] initiateSamlFlow - delegating to OAuth flow (Cognito abstracts SAML):', provider);
    await this.initiateOAuthFlow(provider);
  }

  private getProvider(providerId: string): SSOProvider | undefined {
    const existing = this.providersSubject.value.find(p => p.id === providerId);
    if (existing) return existing;
    
    // For dynamic Cognito IDP names (e.g., dev-salesmail-saml-office365),
    // create a provider entry on the fly so provisionFromCurrentSession doesn't fail
    if (providerId.includes('salesmail-')) {
      console.log('[SSO] Creating dynamic provider entry for:', providerId);
      const dynamicProvider: SSOProvider = {
        id: providerId,
        name: providerId,
        displayName: providerId.includes('saml') ? 'Microsoft Office 365 (SAML)' : 'Microsoft Office 365',
        enabled: true,
        authConfig: {
          scope: 'openid profile email'
        }
      };
      // Add to the list so subsequent lookups find it
      const current = this.providersSubject.value;
      this.providersSubject.next([...current, dynamicProvider]);
      return dynamicProvider;
    }
    
    return undefined;
  }

  // Removed validateTokenWithMicrosoft - Lambda handles this
  // Removed mapEmailToClientId - Lambda handles this via Cognito provider mapping

  async provisionFromCurrentSession(providerId: string): Promise<void> {
    try {
      this.currentProvider = this.getProvider(providerId);
      if (!this.currentProvider) {
        throw new Error(`Unknown SSO provider: ${providerId}`);
      }

      // Ensure we have a Cognito session created by Hosted UI (code flow)
      const session = await this.waitForAmplifySession(15000);
      const idToken = session.getIdToken();
      const accessToken = session.getAccessToken();
      const refreshToken = session.getRefreshToken();

      const claims: any = idToken?.payload || {};

      // Extract actual provider name from Cognito identities claim
      const actualProvider = this.extractProviderFromClaims(claims);
      if (actualProvider) {
        console.log(`[SSO] Detected provider from Cognito: ${actualProvider}`);
      } else {
        console.warn(`[SSO] Could not extract provider from claims, using providerId: ${providerId}`);
      }

      const userInfo = {
        id: claims.sub,
        email: (claims.email || '').toLowerCase(),
        name: claims.name || `${claims.given_name || ''} ${claims.family_name || ''}`.trim(),
        givenName: claims.given_name || '',
        surname: claims.family_name || '',
        jobTitle: claims['custom:job_title'] || '',
        department: claims['custom:department'] || ''
      };

      console.log('[SSO] User already provisioned by post-auth trigger, configuring AWS credentials');
      
      // Even though the post-auth trigger handled user provisioning, we still need to 
      // configure AWS Identity Pool credentials for API calls
      try {
        // Ensure Amplify has the proper session for AWS credential generation
        const currentSession = await Auth.currentSession();
        console.log('[SSO] Current session established:', !!currentSession);
        console.log('[SSO] Session access token scopes:', currentSession?.getAccessToken()?.payload?.scope);
        
        // CRITICAL FIX: Check if SSO user has wrong scopes and force token refresh
        const accessToken = currentSession.getAccessToken();
        const currentScope = accessToken?.payload?.scope;
        
        if (typeof currentScope === 'string' && currentScope.includes('openid') && !currentScope.includes('aws.cognito.signin.user.admin')) {
          console.log('[SSO] Detected SSO user with OIDC scopes, need to get proper Cognito tokens...');
          
          try {
            // Call our backend to generate proper Cognito tokens with the right scopes
            console.log('[SSO] Calling token-exchange to get proper Cognito scopes...');
            
            const tokenExchangeResponse = await this.exchangeForCognitoTokens(currentSession);
            
            if (tokenExchangeResponse && tokenExchangeResponse.cognitoTokens) {
              console.log('[SSO] Got proper Cognito tokens, replacing session...');
              await this.setAmplifySession(tokenExchangeResponse);
              
              // Refresh the session reference
              const newSession = await Auth.currentSession();
              console.log('[SSO] New session scopes:', newSession?.getAccessToken()?.payload?.scope);
            }
            
          } catch (tokenError) {
            console.warn('[SSO] Could not exchange for proper Cognito tokens:', tokenError);
            // Continue anyway - the user might still work with limited functionality
          }
        }
        
        // Force credential refresh to get Identity Pool tokens for API calls
        const credentials = await Auth.currentCredentials();
        console.log('[SSO] AWS credentials configured:', {
          accessKeyId: credentials.accessKeyId?.substring(0, 10) + '...',
          identityId: credentials.identityId,
          authenticated: credentials.authenticated
        });
        
      } catch (credError) {
        console.error('[SSO] Failed to configure AWS credentials:', credError);
        throw new Error('Failed to configure AWS credentials for API access');
      }
      
      // Load the essential authentication context (same as traditional login)
      console.log('[SSO] About to call portalSettingsService.loadData() - this might fail with scope error');
      try {
        // DEBUG: Log final token state before making API calls
        const finalSession = await Auth.currentSession();
        const finalCredentials = await Auth.currentCredentials();
        const finalUserInfo = await Auth.currentUserInfo();
        
        console.log('=== SSO FINAL STATE BEFORE API CALLS ===');
        console.log('Final Access Token:', finalSession?.getAccessToken()?.payload);
        console.log('Final ID Token:', finalSession?.getIdToken()?.payload);
        console.log('Final Credentials:', {
          accessKeyId: finalCredentials.accessKeyId?.substring(0, 10) + '...',
          sessionToken: finalCredentials.sessionToken?.substring(0, 20) + '...',
          identityId: finalCredentials.identityId,
          authenticated: finalCredentials.authenticated
        });
        console.log('Final User Info:', finalUserInfo);
        console.log('=== END SSO FINAL STATE ===');
        
        this.portalSettingsService.loadData();
      } catch (portalErr) {
        console.error('[SSO] portalSettingsService.loadData() failed:', portalErr);
        throw portalErr;
      }
      
      console.log('[SSO] About to call corpService.loadUser() - this might also fail with scope error');
      try {
        this.corpService.loadUser();
      } catch (corpErr) {
        console.error('[SSO] corpService.loadUser() failed:', corpErr);
        throw corpErr;
      }
      
      this.authMonitorService.reLoad();
      this.analyticsService.log('login', `SSO_Success_${providerId}`);

    } catch (error: any) {
      this.analyticsService.log('login', `SSO_Provision_Failed_${providerId}`);
      throw new Error(error?.message || 'Provisioning failed');
    }
  }

  /**
   * Exchange SSO session for proper Cognito tokens with User Pool scopes
   */
  private async exchangeForCognitoTokens(currentSession: any): Promise<SSOTokenExchangeResponse> {
    try {
      // IMPORTANT: Don't call Auth.currentUserInfo() because it requires proper scopes
      // Instead, get user info directly from the ID token payload
      const idToken = currentSession.getIdToken();
      const claims = idToken?.payload || {};
      
      console.log('[SSO] ID Token claims for token exchange:', claims);
      
      // Call the token-exchange Lambda with user info from ID token
      const response = await this.http.post<SSOTokenExchangeResponse>(`${this.apiBaseUrl}/auth/sso/token-exchange`, {
        userInfo: {
          email: claims.email,
          name: claims.name,
          id: claims.sub,
          givenName: claims.given_name,
          surname: claims.family_name,
          validatedAt: new Date().toISOString()
        },
        provider: 'office365',
        client_id: claims['custom:client_id'],
        generateCognitoTokens: true // Special flag to request proper tokens
      }).toPromise();

      console.log('[SSO] Token exchange response:', response);

      // Handle wrapped response from successResponse()
      let result = response;
      if (response && typeof response === 'object' && 'value' in response) {
        result = (response as any).value;
        console.log('[SSO] Unwrapped token exchange response:', result);
      }
      
      if (!result || !result.cognitoTokens) {
        throw new Error('No Cognito tokens returned from exchange');
      }

      return result;
    } catch (error: any) {
      console.error('[SSO] Token exchange error:', error);
      throw error;
    }
  }

  // Helper methods for PKCE generation
  private generateRandomString(length: number): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const base64String = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    return base64String;
  }

  /**
   * CRITICAL FIX: Clean up existing tokens to prevent duplication and scope conflicts
   * This removes any existing OIDC-only tokens or federated username tokens that could
   * cause Amplify to pick the wrong token set on refresh
   */
  private cleanupExistingTokens(userPoolWebClientId: string): void {
    console.log('[SSO] Starting token cleanup to prevent duplication...');
    
    const allKeys = Object.keys(localStorage);
    let cleanedCount = 0;
    
    // Pattern 1: Remove any existing CognitoIdentityServiceProvider tokens (fresh start)
    const cognitoKeys = allKeys.filter(key => key.startsWith(`CognitoIdentityServiceProvider.${userPoolWebClientId}.`));
    
    cognitoKeys.forEach(key => {
      // Keep LastAuthUser, but clean everything else to start fresh
      if (!key.endsWith('.LastAuthUser')) {
        console.log(`[SSO] Removing potentially conflicting token: ${key}`);
        localStorage.removeItem(key);
        cleanedCount++;
      }
    });
    
    // Pattern 2: Clean up any tokens that might have federated usernames in the key
    // (e.g., dev-salesmail-office365_xxx format that could conflict)
    const federatedKeys = allKeys.filter(key => 
      key.includes('CognitoIdentityServiceProvider') && 
      (key.includes('dev-salesmail-office365_') || key.includes('office365_'))
    );
    
    federatedKeys.forEach(key => {
      console.log(`[SSO] Removing federated username token: ${key}`);
      localStorage.removeItem(key);
      cleanedCount++;
    });
    
    // Pattern 3: Clean up any tokens with wrong client IDs that might cause scope issues
    const wrongClientKeys = allKeys.filter(key => 
      key.includes('CognitoIdentityServiceProvider') && 
      !key.includes(userPoolWebClientId) &&
      key.includes('1jrmrd0c6bhu5iq9dpuuiauq4r') // The other client ID we saw in duplicate tokens
    );
    
    wrongClientKeys.forEach(key => {
      console.log(`[SSO] Removing wrong client ID token: ${key}`);
      localStorage.removeItem(key);
      cleanedCount++;
    });
    
    // Pattern 4: Clean up any Amplify flags that might cause confusion
    const amplifyFlags = [
      'amplify-redirected-from-hosted-ui',
      'amplify-signin-with-hostedUI'
    ];
    
    amplifyFlags.forEach(flag => {
      if (localStorage.getItem(flag)) {
        console.log(`[SSO] Cleaning up Amplify flag: ${flag}`);
        localStorage.removeItem(flag);
        cleanedCount++;
      }
    });
    
    console.log(`[SSO] ✅ Token cleanup completed - removed ${cleanedCount} potentially conflicting entries`);
    
    // Verify cleanup
    const remainingCognitoKeys = Object.keys(localStorage)
      .filter(key => key.includes('CognitoIdentityServiceProvider'));
    console.log(`[SSO] Remaining Cognito keys after cleanup:`, remainingCognitoKeys);
  }

  /**
   * PHASE 2: Ensure both identity mapping formats exist to eliminate refresh ambiguity
   * This writes both User Pool ID and Identity Pool ID keyed variants
   */
  private ensureBothIdentityMappingFormats(params: { 
    userPoolId: string; 
    identityPoolId: string; 
    region: string; 
    identityId: string; 
    idToken: string; 
  }) {
    const { userPoolId, identityPoolId, region, identityId, idToken } = params;

    // Provider map uses the User Pool as the login provider
    const providerMap = {
      [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken
    };

    // Format A: keyed by User Pool ID (current format from logs)
    const identityKeyA = `aws.cognito.identity-id.${userPoolId}`;
    const providerKeyA = `aws.cognito.identity-providers.${userPoolId}`;

    // Format B: keyed by Identity Pool ID (Amplify may prefer this on refresh)
    const identityKeyB = `aws.cognito.identity-id.${identityPoolId}`;
    const providerKeyB = `aws.cognito.identity-providers.${identityPoolId}`;

    // Write both formats with the same data
    localStorage.setItem(identityKeyA, identityId);
    localStorage.setItem(providerKeyA, JSON.stringify(providerMap));
    localStorage.setItem(identityKeyB, identityId);
    localStorage.setItem(providerKeyB, JSON.stringify(providerMap));

    console.log('✅ PHASE 2: Stored both identity mapping formats for refresh stability:', {
      userPoolFormat: { identityKeyA, providerKeyA },
      identityPoolFormat: { identityKeyB, providerKeyB },
      identityId,
      providerCount: Object.keys(providerMap).length
    });

    // DEBUG: Check what localStorage keys SSO creates compared to traditional Auth.signIn()
    console.log('🔍 DEBUG: localStorage keys after SSO authentication:');
    const allKeys = Object.keys(localStorage);
    const awsKeys = allKeys.filter(key => key.includes('aws') || key.includes('amplify') || key.includes('cognito') || key.includes('CognitoIdentity'));
    awsKeys.forEach(key => {
      const value = localStorage.getItem(key);
      console.log(`🔍 SSO  ${key}: ${value?.substring(0, 50)}...`);
    });
  }
}
